import { Event } from "event-system";
import { Container, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { GameFont } from "../../../common/GameFont";
import { UIScrollBox } from "../../../common/components/UIScrollBox";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { RenterPolicyResourcePackage } from "../resources/RenterPolicyResourcePackage";
import { RenterPolicyType } from "./RenterPolicyType";
import { VerticalScrollBar } from "../../../common/components/VerticalScrollBar";
import { IRenterPolicyModel } from "../../../common/models/Renters/IRenterPolicyModel";
import { StringFormatExtention } from "../../../../../extensions/StringFormatExtention";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";

export class RenterPolicyFrame extends Container {
    public readonly onFrameButtonClick: Event<void>;
    private readonly renterPolicyResources: RenterPolicyResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly renterPolicyModel: IRenterPolicyModel;
    private readonly frameType: RenterPolicyType;
    private frame: Sprite;
    private frameText: Text;
    private frameLight: Sprite;
    private gotchaButton: ButtonWithScaleAnimation;
    private scrollBar: VerticalScrollBar;
    private scrollBox: UIScrollBox;

    public constructor(
        renterPolicyResources: RenterPolicyResourcePackage,
        commonResources: CommonResourcesPackage,
        renterPolicyModel: IRenterPolicyModel,
        frameType: RenterPolicyType
    ) {
        super();
        this.renterPolicyResources = renterPolicyResources;
        this.commonResources = commonResources;
        this.renterPolicyModel = renterPolicyModel;
        this.frameType = frameType;
        this.onFrameButtonClick = new Event<void>();
        this.build();
    }

    private build(): void {
        this.frameText = new Text("", new FrameInfoTextStyle());
        switch (this.frameType) {
            case RenterPolicyType.DELETED_RENTED:
                this.frame = new Sprite(this.renterPolicyResources.assets_views_renter_policy_images_frame_short_png);
                this.addChild(this.frame);
                this.frameText.text = this.renterPolicyResources.assets_views_renter_policy_renter_text_renter_policy_notification_deleted_txt;
                this.frameText.position.set(39, 98);
                this.addChild(this.frameText);
                break;
            case RenterPolicyType.RENTER_POLICY:
                this.frame = new Sprite(this.renterPolicyResources.assets_views_renter_policy_images_frame_long_png);
                this.addChild(this.frame);
                this.frameText.text = this.renterPolicyResources.assets_views_renter_policy_renter_text_renter_policy_text_txt
                    .replace(/_/g, " ")
                    .replace("[X1]", (StringFormatExtention.formatWithComma(this.renterPolicyModel.configuration.value?.requiredUpgradePoints ?? 0)))
                    .replace("[X2]", (Math.round(this.renterPolicyModel.configuration.value?.durationInHours / 24) ?? 0).toString())
                    .replace(/X3/g, (this.renterPolicyModel.configuration.value?.insuranceDurationInDays ?? 0).toString())
                    .replace("[X4]", (this.renterPolicyModel.configuration.value?.maxSequentialUnActivePeriods ?? 0).toString());
                this.frameText.style.fontSize = 36;
                this.frameText.style.lineHeight = 46;
                this.scrollBox = new UIScrollBox({
                    width: this.frame.width,
                    height: 865,
                    type: 'vertical',
                    vertPadding: 0,
                    horPadding: 30,
                    elementsMargin: 0
                });
                this.scrollBox.position.set(39, 98);
                this.addChild(this.scrollBox);
                this.frameLight = new Sprite(this.renterPolicyResources.assets_views_renter_policy_images_frame_light_png);
                this.frameLight.position.set(16, 750);
                this.addChild(this.frameLight);
                this.scrollBox.addItem(this.frameText);
                this.scrollBar = new VerticalScrollBar({
                    lift: this.commonResources.assets_common_ui_scrollbar_lift_2_png,
                    line: this.commonResources.assets_common_ui_scrollbar_line_2_png
                }, 865, this.frameText.height, 5, -5);
                this.scrollBar.scrollZone = 865;
                this.scrollBar.scrollSize = this.scrollBox.elementsHeight;
                this.scrollBar.position.set(700, 105);
                this.scrollBox.onScroll.add(this.onScroll, this);
                this.addChild(this.scrollBar);
                break;
            case RenterPolicyType.NOTIFICATION_DEFAULT:
                this.frame = new Sprite(this.renterPolicyResources.assets_views_renter_policy_images_frame_mid_png);
                this.addChild(this.frame);
                const tokensValue = Number(this.renterPolicyModel.penalties.value?.tokensAmount).toFixed(6) === "0.000000" ? "0" : Number(this.renterPolicyModel.penalties.value?.tokensAmount).toFixed(6);
                this.frameText.text = this.renterPolicyResources.assets_views_renter_policy_renter_text_renter_policy_notification_txt
                    .replace(/_/g, " ")
                    .replace("[X1]", (this.renterPolicyModel.previousCycle.value?.penaltiesCount ?? 0).toString())
                    .replace("[X2]", (StringFormatExtention.formatWithComma(this.renterPolicyModel.penalties.value?.coinsAmount ?? 0)))
                    .replace("[X3]", (StringFormatExtention.tokensFormat(this.renterPolicyModel.penalties.value?.tokensAmount ?? "0")));
                this.frameText.position.set(39, 93);
                this.addChild(this.frameText);
                break;
        }
        this.gotchaButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        const gotchaButtonPosY = this.frameType == RenterPolicyType.NOTIFICATION_DEFAULT ? 1085 : this.frame.y + this.frame.height - 40 - this.gotchaButton.height;
        this.gotchaButton.position.set(this.frame.x + this.frame.width * 0.5 - this.gotchaButton.width * 0.5, gotchaButtonPosY);
        this.gotchaButton.onUp.add(this.buttonClickHandle, this);
        const buttonText = new Text('GOTCHA', new ButtonTextStyle());
        buttonText.position.set(this.gotchaButton.width * 0.5 - buttonText.width * 0.5, this.gotchaButton.height * 0.5 - buttonText.height * 0.6);
        this.gotchaButton.addChild(buttonText);
        this.addChild(this.gotchaButton);
    }

    private onScroll(value: number): void {
        this.scrollBar.updateScroll(value);
    }

    private async buttonClickHandle(): Promise<void> {
        this.onFrameButtonClick.emit();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.gotchaButton.onUp.remove(this.buttonClickHandle, this);
        super.destroy(options);
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.2,
            dropShadowBlur: 5,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 78,
            miterLimit: 0
        }));
    }
}

class FrameInfoTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "left",
            fill: "#ffffff",
            fontFamily: GameFont.Bebasneuepro_Smerg,
            fontSize: 44,
            lineHeight: 54
        }));
    }
}
